import { ChangeEvent } from 'react'

import { Table, TableProps } from '@mui/material'

import DataTableBody from 'components/Table/Body'
import DataTableHeader from 'components/Table/Header'
import TablePagination from 'components/Table/Pagination'
import { HeadCell } from 'components/Table/types'

export interface SortablePagingParams<T> {
  pageIndex: number
  pageSize: number
  sortField?: keyof T
  sortDirection?: 'desc' | 'asc'
}

interface Props<T> extends TableProps {
  data?: T[]
  headCells: HeadCell<T>[]
  isLoading?: boolean
  pagingParams?: SortablePagingParams<T>
  setPagingParams?: (params: Partial<SortablePagingParams<T>>) => void
  totalPages?: number
  onRowClick?: (row: T) => void
}

const DataTable = <T,>({
  data = [],
  headCells,
  isLoading = false,
  onRowClick,
  pagingParams,
  totalPages = 0,
  setPagingParams,
  sx,
  ...rest
}: Props<T>) => {
  const handlePageChange = (_: ChangeEvent<unknown>, value: number) => {
    setPagingParams && setPagingParams({ pageIndex: value - 1 })
  }

  const getGridTemplateColumns = <T,>(headCells: HeadCell<T>[]) => {
    return headCells.map((cell) => `${cell.flex ?? 1}fr`).join(' ')
  }
  const gridTemplateColumns = getGridTemplateColumns(headCells)

  return (
    <>
      <Table {...rest} sx={sx}>
        <DataTableHeader<T>
          headCells={headCells}
          pagingParams={pagingParams}
          setPagingParams={setPagingParams}
          gridTemplateColumns={gridTemplateColumns}
        />
        <DataTableBody
          data={data}
          isLoading={isLoading}
          onRowClick={onRowClick}
          pagingParams={pagingParams}
          headCells={headCells}
          gridTemplateColumns={gridTemplateColumns}
        />
      </Table>

      {pagingParams && (
        <TablePagination
          page={pagingParams.pageIndex}
          onChange={handlePageChange}
          count={totalPages}
        />
      )}
    </>
  )
}

export default DataTable
